/**
 * Makanly Design System v3.0 - Button Component
 *
 * Variants:
 * - primary: Gray-900 background (dark button for main actions)
 * - secondary: White background with gray border
 * - outline: Transparent with border
 * - ghost: Text-only, no background
 * - danger: Red for destructive actions
 * - link: Underlined text link
 * - accent: Gold for premium/rewards actions
 *
 * @see /docs/design/UI-DESIGN-CONTRACT.md
 */

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        // Primary - Dark gray-900 (Gemini style)
        primary:
          'bg-gray-900 text-white hover:bg-gray-800 active:bg-gray-950 border border-transparent shadow-sm',

        // Default - Maps to primary for backwards compatibility
        default:
          'bg-gray-900 text-white hover:bg-gray-800 active:bg-gray-950 border border-transparent shadow-sm',

        // Secondary - White with gray border
        secondary:
          'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 hover:border-gray-300 active:bg-gray-100 shadow-sm',

        // Outline - Transparent with border
        outline:
          'border border-input bg-background hover:bg-accent/10 hover:text-accent-foreground hover:border-primary/30',

        // Ghost - Text only
        ghost: 'hover:bg-accent/10 hover:text-accent-foreground',

        // Danger - Red for destructive actions
        danger:
          'bg-red-600 text-white hover:bg-red-700 active:bg-red-800 border border-transparent shadow-sm',

        // Destructive - Alias for danger (backwards compatibility)
        destructive:
          'bg-red-600 text-white hover:bg-red-700 active:bg-red-800 border border-transparent shadow-sm',

        // Link - Underlined text
        link: 'text-primary underline-offset-4 hover:underline',

        // Accent - Gold for premium/rewards (NEW in v3.0)
        accent:
          'bg-accent-500 text-gray-900 hover:bg-accent-600 active:bg-accent-700 border border-transparent shadow-sm',

        // Success - Green for positive actions (NEW in v3.0)
        success:
          'bg-emerald-500 text-white hover:bg-emerald-600 active:bg-emerald-700 border border-transparent shadow-sm',

        // Teal - Brand color primary button (NEW in v3.0)
        teal: 'bg-primary-500 text-white hover:bg-primary-600 active:bg-primary-700 border border-transparent shadow-sm',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3 text-xs',
        lg: 'h-11 rounded-md px-8 text-base',
        xl: 'h-12 rounded-lg px-10 text-base', // NEW in v3.0
        icon: 'h-10 w-10',
        'icon-sm': 'h-8 w-8', // NEW in v3.0
        'icon-lg': 'h-12 w-12', // NEW in v3.0
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /**
   * Show loading spinner and disable button
   */
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, loading = false, disabled, children, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <>
            <svg
              className="animate-spin -ml-1 mr-2 h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            {children}
          </>
        ) : (
          children
        )}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
